import { FormEvent, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material"
import CwDataTable from "../../components/construction/CwDataTable"
import CwPageHeader from "../../components/construction/CwPageHeader"
import CwAddButton from "../../components/construction/CwAddButton"
import CompanyChangeAction from "../../components/construction/CompanyChangeAction"
import { useSelectedCompany } from "../../context/companyProvider"
import { getLocale, t } from "../../i18n"
import { formatCompanyContext } from "../../utils/companyAccess"
import { SageApiError } from "../../utils/sageApiError"
import { adminModeService } from "../../services/adminModeService"
import { CwAdminStaffEmployee, CwAdminTask, CwAdminWorkDay } from "../../Interfaces"
import { detailTaskLabel, sortTasks, workDayHours } from "../../utils/adminTimeSheet"
import { formatHours } from "../../utils/jobSiteDailyHours"
import SignInLocationCell from "../../components/construction/SignInLocationCell"
import { isSageCostCode, punchDistanceLabel, punchKindLabel } from "../../utils/punchLocations"

function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function formatTime(value: string | null | undefined): string {
  if (!value) return ""
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString(getLocale() === "es" ? "es-US" : "en-US", { hour: "numeric", minute: "2-digit" })
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof SageApiError && err.message ? err.message : fallback
}

function Section({ title, helper, action, children }: {
  title: string
  helper?: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="mb-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-xl font-semibold">{title}</h2>
          {helper ? <p className="text-sm text-gray-600 font-source">{helper}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

function Loading() {
  return (
    <div className="bg-white rounded-2xl shadow-lg h-32 flex items-center justify-center">
      <CircularProgress />
    </div>
  )
}

function LoadError() {
  return (
    <p className="bg-white rounded-2xl shadow-lg px-5 py-8 text-center text-red-600 font-source">
      {t("adminMode.loadFailed")}
    </p>
  )
}

function TaskFormModal({ companyId, task, open, onClose, onSaved }: {
  companyId: number
  task: CwAdminTask | null
  open: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [nameEn, setNameEn] = useState(task?.nameEn ?? "")
  const [nameEs, setNameEs] = useState(task?.nameEs ?? "")
  const [payrollCode, setPayrollCode] = useState(task?.payrollCode ?? "")
  const [sortOrder, setSortOrder] = useState(task ? String(task.sortOrder) : "")
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!nameEn.trim()) {
      setError(t("adminMode.nameRequired"))
      return
    }
    if (!isSageCostCode(payrollCode)) {
      setError(t("adminMode.payrollCodeRequired"))
      return
    }
    const parsedSort = sortOrder.trim() === "" ? undefined : Number(sortOrder)
    const body = {
      nameEn: nameEn.trim(),
      nameEs: nameEs.trim() || null,
      payrollCode: payrollCode.trim(),
      ...(parsedSort != null && Number.isInteger(parsedSort) ? { sortOrder: parsedSort } : {}),
    }
    setSaving(true)
    setError("")
    try {
      if (task) await adminModeService.updateTask(companyId, task.id, body)
      else await adminModeService.createTask(companyId, body)
      onSaved()
      onClose()
    } catch (err) {
      setError(errorText(err, t("adminMode.saveFailed")))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={(e) => void submit(e)}>
        <DialogTitle>{task ? t("adminMode.editTask") : t("adminMode.addTask")}</DialogTitle>
        <DialogContent className="flex flex-col gap-4 !pt-2">
          <TextField label={t("adminMode.nameEnLabel")} value={nameEn} onChange={(e) => setNameEn(e.target.value)} required autoFocus inputProps={{ maxLength: 100 }} />
          <TextField label={t("adminMode.nameEsLabel")} value={nameEs} onChange={(e) => setNameEs(e.target.value)} inputProps={{ maxLength: 100 }} />
          <TextField label={t("adminMode.payrollCodeLabel")} helperText={t("adminMode.payrollCodeHelper")} value={payrollCode} onChange={(e) => setPayrollCode(e.target.value)} required inputProps={{ maxLength: 50, inputMode: "decimal" }} />
          <TextField label={t("adminMode.sortOrderLabel")} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          {error ? <p role="alert" className="text-red-600 text-sm">{error}</p> : null}
        </DialogContent>
        <DialogActions>
          <button type="button" className="px-4 py-2" onClick={onClose} disabled={saving}>{t("common.cancel")}</button>
          <button type="submit" className="px-4 py-2 rounded-full bg-primary text-white" disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default function AdminModePage() {
  const { selectedCompany, canManageSageMaster } = useSelectedCompany()
  const companyId = selectedCompany?.id
  const queryClient = useQueryClient()
  const locale = getLocale()
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CwAdminTask | null>(null)
  const [actionError, setActionError] = useState("")
  const [from, setFrom] = useState(isoDaysAgo(6))
  const [to, setTo] = useState(isoDaysAgo(0))

  const tasksQuery = useQuery(
    ["cw-admin-tasks", companyId],
    () => adminModeService.listTasks(companyId as number),
    { enabled: !!companyId, suspense: false, useErrorBoundary: false },
  )
  const staffQuery = useQuery(
    ["cw-admin-staff", companyId],
    () => adminModeService.listStaff(companyId as number),
    { enabled: !!companyId, suspense: false, useErrorBoundary: false },
  )
  const rangeValid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to
  const sheetQuery = useQuery(
    ["cw-admin-time-sheet", companyId, from, to],
    () => adminModeService.timeSheet(companyId as number, from, to),
    { enabled: !!companyId && rangeValid, suspense: false, useErrorBoundary: false },
  )

  const tasks = useMemo(() => sortTasks(tasksQuery.data ?? []), [tasksQuery.data])
  const users = staffQuery.data?.users ?? []
  const now = Date.now()

  const toggleActive = async (task: CwAdminTask) => {
    if (!companyId) return
    setActionError("")
    try {
      await adminModeService.updateTask(companyId, task.id, { active: !task.active })
      void queryClient.invalidateQueries(["cw-admin-tasks", companyId])
    } catch (err) {
      setActionError(errorText(err, t("adminMode.saveFailed")))
    }
  }

  const link = async (employee: CwAdminStaffEmployee, value: string) => {
    if (!companyId) return
    setActionError("")
    try {
      await adminModeService.linkStaff(companyId, employee.id, { userId: value ? Number(value) : null })
      void queryClient.invalidateQueries(["cw-admin-staff", companyId])
    } catch (err) {
      setActionError(errorText(err, t("adminMode.saveFailed")))
    }
  }

  const setAdminMode = async (employee: CwAdminStaffEmployee, enabled: boolean) => {
    if (!companyId) return
    setActionError("")
    try {
      await adminModeService.linkStaff(companyId, employee.id, { adminModeEnabled: enabled })
      void queryClient.invalidateQueries(["cw-admin-staff", companyId])
    } catch (err) {
      setActionError(errorText(err, t("adminMode.saveFailed")))
    }
  }

  return (
    <div className="w-full">
      <CwPageHeader
        title={t("adminMode.title")}
        subtitle={selectedCompany ? `${formatCompanyContext(selectedCompany)} · ${t("adminMode.subtitle")}` : t("adminMode.subtitle")}
        action={<CompanyChangeAction />}
      />
      {actionError ? <p role="alert" className="text-red-600 mb-4 font-source">{actionError}</p> : null}

      <Section
        title={t("adminMode.tasksTitle")}
        helper={t("adminMode.tasksHelper")}
        action={canManageSageMaster && companyId ? (
          <CwAddButton label={t("adminMode.addTask")} onClick={() => { setEditing(null); setFormOpen(true) }} />
        ) : null}
      >
        {tasksQuery.isLoading ? <Loading /> : tasksQuery.error ? <LoadError /> : (
          <CwDataTable<CwAdminTask>
            columns={[
              { key: "nameEn", label: t("adminMode.columnTask") },
              { key: "nameEs", label: t("adminMode.columnSpanish"), render: (row) => row.nameEs ?? "" },
              { key: "payrollCode", label: t("adminMode.columnPayrollCode"), render: (row) => row.payrollCode ?? "" },
              { key: "active", label: t("adminMode.columnStatus"), render: (row) => (row.active ? t("adminMode.active") : t("adminMode.inactive")) },
              ...(canManageSageMaster ? [{
                key: "actions",
                label: t("common.actions"),
                render: (row: CwAdminTask) => (
                  <div className="flex gap-3">
                    <button type="button" className="text-primary underline" onClick={() => { setEditing(row); setFormOpen(true) }}>
                      {t("common.edit")}
                    </button>
                    <button type="button" className="text-primary underline" onClick={() => void toggleActive(row)}>
                      {row.active ? t("adminMode.deactivate") : t("adminMode.reactivate")}
                    </button>
                  </div>
                ),
              }] : []),
            ]}
            rows={tasks}
            empty={t("adminMode.tasksEmpty")}
          />
        )}
      </Section>

      <Section title={t("adminMode.staffTitle")} helper={t("adminMode.staffHelper")}>
        {staffQuery.isLoading ? <Loading /> : staffQuery.error ? <LoadError /> : (
          <CwDataTable<CwAdminStaffEmployee>
            columns={[
              { key: "name", label: t("adminMode.columnEmployee"), render: (row) => `${row.firstName} ${row.lastName}`.trim() || `#${row.employeeNum}` },
              {
                key: "adminModeEnabled",
                label: t("adminMode.columnAdminMode"),
                render: (row) => (
                  <span className="flex flex-col">
                    {canManageSageMaster ? (
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          aria-label={`${t("adminMode.columnAdminMode")} ${row.firstName} ${row.lastName}`}
                          checked={!!row.adminModeEnabled}
                          onChange={(e) => void setAdminMode(row, e.target.checked)}
                        />
                        {row.adminModeEnabled ? t("adminMode.adminModeOn") : t("adminMode.adminModeOff")}
                      </label>
                    ) : (row.adminModeEnabled ? t("adminMode.adminModeOn") : t("adminMode.adminModeOff"))}
                    {row.adminModeEnabled && !row.userId ? (
                      <span className="text-amber-700 text-sm">{t("adminMode.needsLogin")}</span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: "login",
                label: t("adminMode.columnLogin"),
                render: (row) => (canManageSageMaster ? (
                  <select
                    aria-label={`${t("adminMode.columnLogin")} ${row.firstName} ${row.lastName}`}
                    className="border rounded px-2 py-1"
                    value={row.userId ?? ""}
                    onChange={(e) => void link(row, e.target.value)}
                  >
                    <option value="">{t("adminMode.notLinked")}</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {[u.firstName, u.lastName].filter(Boolean).join(" ")}{u.email ? ` (${u.email})` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  (() => {
                    if (row.userId == null) return t("adminMode.notLinked")
                    const u = users.find((x) => x.id === row.userId)
                    if (!u) return `#${row.userId}`
                    const name = [u.firstName, u.lastName].filter(Boolean).join(" ")
                    return u.email ? (name ? `${name} (${u.email})` : u.email) : (name || `#${row.userId}`)
                  })()
                )),
              },
            ]}
            rows={staffQuery.data?.employees ?? []}
            empty={t("adminMode.staffEmpty")}
          />
        )}
      </Section>

      <Section
        title={t("adminMode.timeSheetTitle")}
        action={(
          <div className="flex gap-3 items-center font-source">
            <label className="flex items-center gap-2">
              {t("payroll.fromLabel")}
              <input type="date" className="border rounded px-2 py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="flex items-center gap-2">
              {t("payroll.toLabel")}
              <input type="date" className="border rounded px-2 py-1" value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
          </div>
        )}
      >
        {!rangeValid ? (
          <p className="text-red-600 font-source">{t("payroll.dateOrder")}</p>
        ) : sheetQuery.isLoading ? <Loading /> : sheetQuery.error ? <LoadError /> : (
          <CwDataTable<CwAdminWorkDay>
            columns={[
              { key: "date", label: t("adminMode.columnDate"), render: (row) => String(row.date).slice(0, 10) },
              { key: "employee", label: t("adminMode.columnEmployee"), render: (row) => [row.employee?.firstName, row.employee?.lastName].filter(Boolean).join(" ") },
              { key: "in", label: t("adminMode.columnClockIn"), render: (row) => formatTime(row.clockedInAt) },
              {
                key: "out",
                label: t("adminMode.columnClockOut"),
                render: (row) => (row.clockedOutAt
                  ? formatTime(row.clockedOutAt)
                  : row.autoEndedAt
                    ? `${formatTime(row.autoEndedAt)} · ${t("adminMode.autoEnded")}`
                    : t("adminMode.stillClockedIn")),
              },
              {
                key: "tasks",
                label: t("adminMode.columnTasks"),
                render: (row) => (
                  <ul className="list-none m-0 p-0">
                    {(row.details ?? []).map((d) => (
                      <li key={d.id}>
                        {detailTaskLabel(d, locale, t("adminMode.custom"))}
                        {d.startedAt ? <span className="text-gray-500"> · {formatTime(d.startedAt)}</span> : null}
                      </li>
                    ))}
                  </ul>
                ),
              },
              { key: "hours", label: t("adminMode.columnHours"), render: (row) => formatHours(workDayHours(row, now)) },
              { key: "location", label: t("adminMode.columnLocation"), render: (row) => <SignInLocationCell source={row} /> },
              {
                key: "punches",
                label: t("adminMode.columnPunches"),
                render: (row) => (
                  <ul className="list-none m-0 p-0">
                    {(row.punchLocations ?? []).filter((p) => p.kind !== "CLOCK_IN").map((p) => (
                      <li key={p.id} className={p.flaggedFar ? "text-amber-700" : undefined}>
                        {punchKindLabel(p.kind)} · {formatTime(p.capturedAt ?? p.createdAt)}
                        {p.latitude != null && p.longitude != null ? (
                          <>
                            {" · "}
                            <a className="text-primary underline" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps/search/?api=1&query=${p.latitude},${p.longitude}`}>
                              {t("signInLocation.openMap")}
                            </a>
                          </>
                        ) : p.status ? ` · ${p.status.toLowerCase() === "denied" ? t("signInLocation.denied") : p.status.toLowerCase() === "timeout" ? t("signInLocation.timeout") : t("signInLocation.unavailable")}` : null}
                        {p.flaggedFar ? ` · ${t("timeClock.farFlag")} (${punchDistanceLabel(p)})` : null}
                      </li>
                    ))}
                  </ul>
                ),
              },
            ]}
            rows={sheetQuery.data ?? []}
            empty={t("adminMode.timeSheetEmpty")}
          />
        )}
      </Section>

      {companyId && formOpen ? (
        <TaskFormModal
          key={editing?.id ?? "new"}
          companyId={companyId}
          task={editing}
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditing(null) }}
          onSaved={() => void queryClient.invalidateQueries(["cw-admin-tasks", companyId])}
        />
      ) : null}
    </div>
  )
}
